import { useEffect } from "react";
import { BASE_OF, CONSOLE, Client, ORIGIN_OF, PRODUCT, Contact, LeadCta, Router, apiBase, leadTransport, match, ownerOf, usePageViews, useRoute, useTheme, useLeadModal } from "@authoxi/ui-shared";
import { TOOLS } from "./tools";
import { Badge } from "./views/Badge";
import { DirectoryAgent, DirectoryHost, DirectoryIndex } from "./views/Directory";
import { Grader } from "./views/Grader";
import { Index } from "./views/Index";
import { Leaderboard } from "./views/Leaderboard";
import { Observe } from "./views/Observe";
import { Receipt } from "./views/Receipt";
import { Report } from "./views/Report";
import { Signin } from "./views/Signin";
import { Spec } from "./views/Spec";
import { Verify } from "./views/Verify";
import { Whois } from "./views/Whois";

/**
 * Every route, in one place — the prerenderer walks this list, and so does the sitemap.
 *
 * These pages ARE the acquisition surface: somebody searches "is my agent identifiable", lands
 * on the grader, and that is the whole funnel. Until this list existed they shipped as one empty
 * `<div id="root">` to every crawler and every LLM fetcher, which is not a degraded page — it is
 * no page at all to anything that does not run JavaScript, and most AI crawlers do not.
 *
 * Parameterised routes (`/directory/:host`) are deliberately absent: their content comes from
 * the live API per host, so there is nothing to freeze at build time. The directory's own
 * server-rendered sitemap covers those.
 */
export const ROUTES: [path: string, render: () => JSX.Element][] = [
  ["/", () => <Index />],
  ["/contact", () => <ContactView />],
  ["/grader", () => <Grader />],
  ["/badge", () => <Badge />],
  ["/leaderboard", () => <Leaderboard />],
  ["/directory", () => <DirectoryIndex />],
  ["/whois", () => <Whois />],
  ["/report", () => <Report />],
  ["/observe", () => <Observe />],
  ["/receipt", () => <Receipt />],
  ["/verify", () => <Verify />],
  ["/spec", () => <Spec />],
  ["/signin", () => <Signin />],
];

export default function App() {
  return (
    // The mount point, read from the file that declares it rather than typed — this base and
    // vite's disagreeing is exactly the bug that broke 29 links last time, and it is the kind
    // that never throws: a base the host does not have gets re-added on every pushState and
    // stripped again on the next load, so it repairs itself every time and leaves a wrong,
    // non-canonical URL in the bar and in everything anyone copies.
    // `scripts/crosslinks.mjs` fails the build if these two ever disagree again.
    //
    // Links INSIDE this app stay base-less (`/grader`, not `/tools/grader`): the router strips
    // the base off an href before matching and re-adds it on pushState, so a prefixed href
    // would work by accident today and break the day the mount point moves.
    <Router base={BASE_OF.gtm}>
      <Shell />
    </Router>
  );
}

function Shell() {
  const { path } = useRoute();
  usePageViews(path);
  const [, toggleTheme] = useTheme();
  const lead = useLeadModal();

  const view =
    match(path, [
      // ROUTES first — the prerendered set. Then the parameterised ones, which cannot be
      // prerendered because their content is per-host and comes from the live API.
      ...ROUTES,
      // Literal before parameterised: the first match wins, so `/directory` (in ROUTES above)
      // must be listed ahead of `/directory/:host` or the index would never render. The same
      // ordering trap that once made `/directory/{host}.json` unreachable on the server.
      ["/directory/agent/:id", (p) => <DirectoryAgent id={p.id} />],
      ["/directory/:host", (p) => <DirectoryHost host={p.host} />],
    ]) ?? <NotFound path={path} />;

  return (
    <>
      <header className="top">
        <div className="top-in">
          {/* SPLIT, because one mark was being asked to mean two things. The wordmark is the
              company and goes to the company — a visitor who lands on a tool from search and
              clicks "authoxi" is asking who made this, and answering with the tool index is a
              dead end at the exact moment they became interested. The sub-label is the surface
              they are actually on, so that keeps the tools index. */}
          <span className="brand">
            <a href={PRODUCT}>
              <i aria-hidden="true" />
              authoxi
            </a>
            <a href="/tools" className="brand-sub">
              <small>free tools</small>
            </a>
          </span>
          <div className="grow" />
          <span className="pill top-badge">
            <span className="dot" style={{ color: "var(--allow)" }} />
            No account needed
          </span>
          <button className="theme-btn" onClick={toggleTheme} aria-label="Switch theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 13.2A8 8 0 0 1 10.8 4a8 8 0 1 0 9.2 9.2Z" />
            </svg>
          </button>
          <a className="btn btn-go top-cta" href={`${PRODUCT}/start`}>
            Get a key
          </a>
        </div>
      </header>

      <div className="shell">
        <Rail path={path} onTalk={() => lead.open({ source: "contact" })} />
        <main id="view">{view}</main>
      </div>

      {/* ONE dialog for the whole shell, rather than one per trigger. A modal rendered beside
          each button is a modal whose open state is duplicated as many times as there are
          buttons — and the second one somebody adds forgets to close the first. */}
      <lead.Modal
        transport={leadTransport(new Client({ baseUrl: apiBase() }))}
        product="authoxi"
        variant="qualified"
        copy={{
          title: "Talk to us",
          body: "A question, a use case, or something that should work and does not. One reply, from a person who builds it.",
          cta: "Send",
          done: "Got it — we will reply, usually the same day.",
        }}
      />

      {/* Most first visits land here, and every tool has an email field. The legal links have
          to be reachable from this page, not only from a marketing site nobody may see. */}
      <footer className="gtm-legal">
        <div className="gtm-legal-in">
          <span>© 2026 authoxi. All rights reserved.</span>
          <span className="gtm-legal-links">
            <a href={`${PRODUCT}/privacy`}>Privacy</a>
            <a href={`${PRODUCT}/terms`}>Terms</a>
            <a href={PRODUCT}>The product</a>
          </span>
        </div>
      </footer>
    </>
  );
}

function Rail({ path, onTalk }: { path: string; onTalk: () => void }) {
  return (
    <aside className="rail" id="rail">
      {TOOLS.map((group) => (
        <div className="rail-group" key={group.group}>
          <h6>{group.group}</h6>
          {group.items.map((t) => (
            <a
              key={t.id}
              href={`/tools/${t.id}`}
              className={path.startsWith(`/${t.id}`) ? "on" : ""}
            >
              {t.name}
            </a>
          ))}
        </div>
      ))}
      {/* The way out. Somebody who arrived on a tool from search has no other route to the
          product, and a free surface that cannot reach the thing it is free in service of is
          a utility somebody else gets to monetise. */}
      <div className="rail-group rail-out">
        <h6>Elsewhere</h6>
        <a href={PRODUCT}>The product</a>
        <a href={`${PRODUCT}/pricing`}>Pricing</a>
        {/* A MODAL RATHER THAN A NAVIGATION, and only here. Somebody reading a tool result has
            a reason to be on this page; sending them to /tools/contact spends that. The
            post-result gates stay inline — that placement converts better and it is the rule.
            `trigger: "cta"` on the event is what keeps the two measurable apart. */}
        <LeadCta className="rail-link" onOpen={onTalk}>
          Talk to us
        </LeadCta>
        <a href={CONSOLE} rel="external">
          Console
        </a>
      </div>
    </aside>
  );
}

/**
 * Not found — but first, check whether another surface owns this path.
 *
 * `/tools/console` answering "No such tool" is the least useful thing it could say: the path is
 * real, it is just not ours. Anyone arriving from an old link, a bookmark, or a search result
 * gets the page they asked for instead of a dead end.
 *
 * `path` here is already base-stripped, so this is comparing `/console` against the ownership
 * map, not `/tools/console`. Reaching it at all takes a client-side navigation or a typed URL —
 * a cold request to `/why` never loads this bundle, because the edge routes anything outside
 * `/tools/*` to the marketing build before a byte of JavaScript runs.
 *
 * `replace`, not `assign`, so the wrong host never enters history and Back goes where the
 * visitor expects rather than bouncing through the redirect again.
 */
function NotFound({ path }: { path: string }) {
  const owner = ownerOf(path);

  useEffect(() => {
    if (owner && owner !== "gtm") location.replace(ORIGIN_OF[owner] + path);
  }, [owner, path]);

  if (owner && owner !== "gtm") {
    return (
      <div className="tool-head">
        <h1>Taking you there…</h1>
        <p className="lede">
          That page lives on <a href={ORIGIN_OF[owner] + path}>{ORIGIN_OF[owner] + path}</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="tool-head">
      <h1>No such tool.</h1>
      <p className="lede">
        The eleven that exist are in the rail. <a href="/tools/grader">Start with the grader.</a>
      </p>
    </div>
  );
}

function ContactView() {
  return (
    <Contact
      source="gtm"
      surface="tools-contact"
      heading="Talk to us"
      blurb="You have used the free tools. If something should work and does not, or you want the
             paid version of what you just saw, this reaches a person."
    />
  );
}
